import { gql } from '@apollo/client';

import type { ApolloCache } from '@apollo/client';

/**
 * TA-10878: sending into a chat that sits below the first page left the list
 * untouched — the chat neither moved to the top nor showed its new message.
 *
 * `bumpChatToTop` refuses to prepend a synthetic edge for a Chat the cached
 * `listChatsPagination` read cannot complete (REQUIRED_LIST_ROW_FIELDS), and
 * `useSendMessage` ignored that refusal on the assumption that "the chat is
 * open, so GetChatDetail has already cached every field the row needs". It
 * has not. Five of the twenty fields the list selects are cached by
 * GetChatDetail under NESTED names instead:
 *
 *     settingsPin        ← settings.pin
 *     settingsMute       ← settings.mute
 *     settingsArchivedAt ← settings.archivedAt
 *     ownerId            ← owner.id
 *     ownerName          ← owner.name
 *
 * So the guard always failed for an off-page chat, no edge was ever
 * prepended, and the send path had no fallback. The data is already in the
 * cache; only the field names differ. Copying them across lets the guard pass
 * and the bump place the chat with a complete read — no network round trip,
 * which is the whole point of the synthetic edge (a refetch at send time
 * would answer from before the message existed anyway).
 */
const CHAT_NESTED_ROW_SOURCE = gql`
  fragment ChatNestedRowSource on Chat {
    settings {
      id
      pin
      mute
      archivedAt
    }
    owner {
      id
      name
    }
  }
`;

const CHAT_FLAT_ROW_FIELDS = gql`
  fragment ChatFlatRowFields on Chat {
    settingsPin
    settingsMute
    settingsArchivedAt
    ownerId
    ownerName
  }
`;

type NestedRowSource = {
  settings?: {
    pin?: boolean | null;
    mute?: boolean | null;
    archivedAt?: string | null;
  } | null;
  owner?: { id?: string | null; name?: string | null } | null;
};

type FlatRowFields = {
  settingsPin?: boolean | null;
  settingsMute?: boolean | null;
  settingsArchivedAt?: string | null;
  ownerId?: string | null;
  ownerName?: string | null;
};

/**
 * The list-row fields this helper supplies. Exported so a test can check
 * them against `REQUIRED_LIST_ROW_FIELDS` and the production `getChat`
 * selection — the two together have to cover every field the row needs, or
 * the guard in `bumpChatToTop` can never pass for a detail-cached chat.
 */
export const HYDRATED_ROW_FIELDS = [
  'settingsPin',
  'settingsMute',
  'settingsArchivedAt',
  'ownerId',
  'ownerName',
] as const;

/**
 * Fills in the flat chat-list row fields of a Chat that was cached by
 * `GetChatDetail`, deriving them from the nested `settings` / `owner` the
 * detail query does cache. Returns true when something was written.
 *
 * Writes nothing when the row fields are already there (the chat came from
 * the list itself) and when the nested source is incomplete — a guessed
 * `ownerId` would be worse than the refetch fallback.
 */
export const hydrateChatRowFields = (
  cache: ApolloCache<any>,
  chatId: string
): boolean => {
  const chatCacheId = cache.identify({ __typename: 'Chat', id: chatId });
  if (!chatCacheId) return false;

  // returnPartialData: without it a fragment read of a chat missing any of
  // these fields comes back null, which is the case this helper exists for.
  const flat =
    cache.readFragment<FlatRowFields>({
      id: chatCacheId,
      fragment: CHAT_FLAT_ROW_FIELDS,
      returnPartialData: true,
    }) ?? {};
  if (HYDRATED_ROW_FIELDS.every((field) => flat[field] !== undefined))
    return false;

  const nested = cache.readFragment<NestedRowSource>({
    id: chatCacheId,
    fragment: CHAT_NESTED_ROW_SOURCE,
    returnPartialData: true,
  });
  // A chat with no UserChatSetting row — the common case for one nobody has
  // pinned, muted or archived, which is exactly the long-quiet chat this
  // exists for — comes back with `settings: null`. That is not missing data:
  // no row means not pinned, not muted, not archived. Only the owner has no
  // safe default, and `Chat.owner` is non-null in the schema, so its absence
  // means the chat was never cached in detail form at all.
  const settings = nested?.settings;
  const owner = nested?.owner;
  if (!owner?.id || owner.name == null) return false;

  cache.writeFragment({
    id: chatCacheId,
    fragment: CHAT_FLAT_ROW_FIELDS,
    data: {
      __typename: 'Chat',
      settingsPin: !!settings?.pin,
      settingsMute: !!settings?.mute,
      settingsArchivedAt: settings?.archivedAt ?? null,
      ownerId: owner.id,
      ownerName: owner.name,
    },
  });

  return true;
};
